import { c as _c } from "react/compiler-runtime";
import { Toggle as TogglePrimitive } from "@base-ui/react/toggle";
import { ToggleGroup as ToggleGroupPrimitive } from "@base-ui/react/toggle-group";
import { createContext, useContext, useMemo } from "react";
import type { VariantProps } from "../utils";
import { cn } from "../utils";
import { toggleVariants } from "./toggle";
const ToggleGroupContext = createContext<VariantProps<typeof toggleVariants> & {
  spacing?: number;
  orientation?: "horizontal" | "vertical";
}>({
  size: "default",
  variant: "default",
  spacing: 0,
  orientation: "horizontal"
});
function ToggleGroup(t0) {
  const $ = _c(29);
  let children;
  let className;
  let props;
  let size;
  let t1;
  let t2;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      size,
      spacing: t1,
      orientation: t2,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = size;
    $[5] = t1;
    $[6] = t2;
    $[7] = variant;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    size = $[4];
    t1 = $[5];
    t2 = $[6];
    variant = $[7];
  }
  const spacing = t1 === undefined ? 0 : t1;
  const orientation = t2 === undefined ? "horizontal" : t2;
  let t3;
  if ($[8] !== orientation || $[9] !== size || $[10] !== spacing || $[11] !== variant) {
    t3 = {
      variant,
      size,
      spacing,
      orientation
    };
    $[8] = orientation;
    $[9] = size;
    $[10] = spacing;
    $[11] = variant;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const contextValue = t3;
  let t4;
  if ($[13] !== spacing) {
    t4 = {
      "--gap": spacing
    };
    $[13] = spacing;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const t5 = t4 as React.CSSProperties;
  let t6;
  if ($[15] !== className) {
    t6 = cn("group/toggle-group flex w-fit flex-row items-center gap-[--spacing(var(--gap))] rounded-md data-[orientation=vertical]:flex-col data-[orientation=vertical]:items-stretch data-[spacing=0]:data-[variant=outline]:shadow-xs", className);
    $[15] = className;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== children || $[18] !== contextValue) {
    t7 = <ToggleGroupContext.Provider value={contextValue}>{children}</ToggleGroupContext.Provider>;
    $[17] = children;
    $[18] = contextValue;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== orientation || $[21] !== props || $[22] !== size || $[23] !== spacing || $[24] !== t5 || $[25] !== t6 || $[26] !== t7 || $[27] !== variant) {
    t8 = <ToggleGroupPrimitive data-slot="toggle-group" data-variant={variant} data-size={size} data-spacing={spacing} data-orientation={orientation} style={t5} className={t6} {...props}>{t7}</ToggleGroupPrimitive>;
    $[20] = orientation;
    $[21] = props;
    $[22] = size;
    $[23] = spacing;
    $[24] = t5;
    $[25] = t6;
    $[26] = t7;
    $[27] = variant;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
}
function ToggleGroupItem(t0) {
  const $ = _c(19);
  let children;
  let className;
  let props;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      className,
      children,
      variant: t1,
      size: t2,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
    $[5] = t2;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const variant = t1 === undefined ? "default" : t1;
  const size = t2 === undefined ? "default" : t2;
  const context = useContext(ToggleGroupContext);
  const t3 = context.variant || variant;
  const t4 = context.size || size;
  const t5 = context.spacing;
  let t6;
  if ($[6] !== className || $[7] !== context.size || $[8] !== context.variant || $[9] !== size || $[10] !== variant) {
    t6 = cn("shrink-0 group-data-[spacing=0]/toggle-group:rounded-none group-data-[spacing=0]/toggle-group:px-2 group-data-[spacing=0]/toggle-group:shadow-none focus:z-10 focus-visible:z-10 group-data-horizontal/toggle-group:data-[spacing=0]:first:rounded-l-md group-data-vertical/toggle-group:data-[spacing=0]:first:rounded-t-md group-data-horizontal/toggle-group:data-[spacing=0]:last:rounded-r-md group-data-vertical/toggle-group:data-[spacing=0]:last:rounded-b-md data-[state=on]:bg-muted group-data-horizontal/toggle-group:data-[spacing=0]:data-[variant=outline]:border-l-0 group-data-vertical/toggle-group:data-[spacing=0]:data-[variant=outline]:border-t-0 group-data-horizontal/toggle-group:data-[spacing=0]:data-[variant=outline]:first:border-l group-data-vertical/toggle-group:data-[spacing=0]:data-[variant=outline]:first:border-t", toggleVariants({
      variant: context.variant || variant,
      size: context.size || size
    }), className);
    $[6] = className;
    $[7] = context.size;
    $[8] = context.variant;
    $[9] = size;
    $[10] = variant;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== children || $[13] !== context.spacing || $[14] !== props || $[15] !== t3 || $[16] !== t4 || $[17] !== t6) {
    t7 = <TogglePrimitive data-slot="toggle-group-item" data-variant={t3} data-size={t4} data-spacing={t5} className={t6} {...props}>{children}</TogglePrimitive>;
    $[12] = children;
    $[13] = context.spacing;
    $[14] = props;
    $[15] = t3;
    $[16] = t4;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
}
export { ToggleGroup, ToggleGroupItem };